import { useState, useEffect, useRef } from 'react'
import { Form, Formik, type FormikHelpers, type FormikProps } from 'formik'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import { Box, Button, Grid } from '@dhl-ui/components'
import { getData } from '~/api/admin-api'

import { TrackerCodeSearchField } from '~/common'
import { useSearchQuery } from '~/common/hooks'
import type { LabelRequestDto } from '~/modules/label/model'
import { LabelRequest, LabelRequestSkeleton } from '~/modules/label/components'

type FormValues = {
  trackerCode: string
}

export const LabelPage = () => {
  const { t } = useTranslation()
  const labelPid = useSearchQuery('labelPid')
  const [labelRequest, setLabelRequest] = useState<LabelRequestDto>()
  const formRef = useRef<FormikProps<FormValues>>(null)

  const initialValues: FormValues = {
    trackerCode: labelPid ?? '',
  }

  // This effect is purely for the purpose of submitting the form when the labelPid is present in the URL
  useEffect(() => {
    if (formRef.current && !!labelPid) {
      formRef.current.submitForm().then(() => {})
    }
  }, [labelPid])

  const createValidationSchema = (t: TFunction) =>
    Yup.object({
      trackerCode: Yup.string().required(t('admin.labelPage.inputTrackerCodeMessage')).default(''),
    }).defined()

  const onSubmit = async (values: FormValues, helpers: FormikHelpers<FormValues>) => {
    const trackerCode = values.trackerCode.toUpperCase()

    try {
      const response = await getData<LabelRequestDto>(`admin-label-requests?trackerCode=${trackerCode}`)

      if (response) {
        setLabelRequest(response)
      } else {
        helpers.setFieldError('trackerCode', `${t('admin.labelPage.helperText')} ${trackerCode}`)
        setLabelRequest(undefined)
      }
      helpers.setSubmitting(false)
    } catch {
      helpers.setFieldError('trackerCode', `${t('admin.labelPage.helperText')} ${trackerCode}`)
      setLabelRequest(undefined)
    }
  }

  const validationSchema = createValidationSchema(t)

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={validationSchema} innerRef={formRef}>
      {({ isSubmitting }) => (
        <Form data-testid="label-form">
          <Box pt={2} pb={4}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 4 }}>
                <TrackerCodeSearchField />
              </Grid>
              <Grid size={{ xs: 3 }}>
                <Button
                  data-testid="submit-labelRequest"
                  disabled={isSubmitting}
                  color="primary"
                  type="submit"
                  variant="contained"
                  size="medium"
                >
                  {t('admin.labelPage.trackerCodeDownloadButton')}
                </Button>
              </Grid>
            </Grid>
            {labelRequest ? <LabelRequest labelRequest={labelRequest} /> : <LabelRequestSkeleton />}
          </Box>
        </Form>
      )}
    </Formik>
  )
}
